<template>
  <div>
    <validation-provider
      #default="{ errors }"
      :name="errorName"
      :rules="rules"
    >
      <b-form-group :state="errors.length > 0 ? false : null">
        <label :for="id">{{ label }}</label>
        <v-select
          :id="id"
          v-model="innerValue"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="options"
          :placeholder="placeholder"
          :label="item"
          :clearable="clearable"
          :value="value"
          :disabled="readonly"
          :reduce="reduce"
          :multiple="multiple"
          @input="handleInput"
        >
          <template v-slot:spinner="{}">
            <b-spinner
              v-if="loading"
              variant="primary"
              small
            />
          </template>
        </v-select>

        <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
          {{ errors[0] }}
        </b-form-invalid-feedback>
      </b-form-group>
    </validation-provider>
  </div>
</template>

<script>
import { BFormGroup, BFormInvalidFeedback, BSpinner } from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'

export default {
  name: 'SelectField',
  components: {
    BFormGroup,
    BSpinner,
    BFormInvalidFeedback,
    vSelect,
    ValidationProvider,
  },
  props: {
    label: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorName: {
      type: String,
      default: 'default',
    },
    rules: {
      type: [Object, String],
      default: '',
    },
    id: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: false,
      default: () => [],
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    reduce: {
      type: Function,
      default: null,
    },
    item: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    value: {
      type: [Object, String, Number, Boolean, Array],
      required: false,
      default: null,
    },
    setSelected: {
      type: Function,
      required: false,
      default: null,
    },
    optionStyleFunction: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      innerValue: '',
      isMounted: false,
    }
  },
  computed: {
    hasValue() {
      return !!this.innerValue
    },
  },
  watch: {
    innerValue(value) {
      this.$emit('input', value)
    },
    value(val) {
      if (val !== this.innerValue) {
        this.innerValue = val
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.isMounted = true // ← flips after initial render
    })
  },
  created() {
    if (this.value !== null && this.value !== undefined) {
      this.innerValue = this.value
    }
  },
  methods: {
    handleInput(value) {
      if (this.setSelected && this.isMounted) {
        this.setSelected(value)
      }
    },
  },
}
</script>

<style lang="scss">
// Import vue-select's base styles
@import "@core/scss/vue/libs/vue-select.scss";

// Hover state styling
.v-select .vs__dropdown-option--highlight {
  background: rgba(92, 95, 94, 0.08) !important;
  color: var(--primary) !important;
}

// Selected state styling
.v-select .vs__dropdown-option--selected {
  background: var(--primary) !important;
  color: white !important;

  // For the checkmark that appears in the screenshot (if it's a multiple select):
  // You might need to adjust the color of the checkmark icon,
  // which is often an SVG or pseudo-element.
  // Example for common checkmark icon:
  .vs__selected-option:after {
    color: var(
      --primary
    ) !important; // Adjust checkmark color if it's an icon/pseudo-element
  }
}

// Overriding font-family for vue-select internal text elements
// This ensures that the dynamic font-family applied via inline style (from optionStyleFunction)
// is not overridden by vue-select's default CSS.
.v-select .vs__dropdown-option span,
.v-select .vs__selected-option span {
  font-family: var(--font-family-primary) !important;
}

// Also ensure placeholder and search input use the correct dynamic font
.v-select .vs__search::placeholder,
.v-select .vs__selected,
.v-select .vs__search {
  font-family: var(--font-family-primary) !important;
}
</style>
